import { useState } from "react";
import { Form } from "react-final-form";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "src/store";
import { selectCurrentWorkspaceId } from "src/store/app";
import { CreateRolloutPayload } from "src/ui/containers/dashboard/rollouts/api/rtkClient";
import { useValidationSchema } from "src/ui/utils/useValidationSchema";

import { closeDetails, selectCreateRolloutDetails } from "@dashboard/devices/store";
import { useCreateRolloutMutation, useGetOtaFilesQuery } from "@dashboard/rollouts/api";
import { Box, Step, StepLabel, Stepper, Typography } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import SemverService from "@services/semverService/semverService";
import { SecondaryButton } from "@shared/CustomButton";
import { showErrorToast, showSuccessToast } from "@shared/toasts/Toasts";

import { PaneElement } from "../shared/PaneElement";
import { useStyle } from "../shared/style";

import { ConfirmStep } from "./ConfirmStep/ConfirmStep";
import { DeviceStep } from "./DeviceStep/DeviceStep";
import { scheduleValidationSchema } from "./ScheduleStep/schedule.validation";
import { ScheduleStep } from "./ScheduleStep/ScheduleStep";
import { useStyles } from "./CreateRolloutSidePanel.styles";
import { getSelectedDeviceIds, RolloutFormValues, ScheduleType } from "./CreateRolloutSidePanel.utils";
import { VersionMeta } from "./VersionMeta";

enum RolloutStep {
    Devices = 0,
    Schedule = 1,
    Confirm = 2,
}

export const CreateRolloutSidePanel = () => {
    const [activeStep, setActiveStep] = useState(RolloutStep.Devices);

    const { classes } = useStyle();
    const dispatch = useDispatch();
    const { classes: localClasses } = useStyles();
    const { t } = useTranslation(["sidepanels", "common"]);

    const workspaceId = useSelector(selectCurrentWorkspaceId);
    const details = useSelector(selectCreateRolloutDetails);

    const majorVersion = details ? SemverService.getMajorVersion(details.version) : null;
    const { data: otaData } = useGetOtaFilesQuery(
        details?.codename && workspaceId !== null
            ? {
                  codename: details.codename,
                  workspaceId,
                  pageSize: 200,
                  includeMajorVersions: majorVersion !== null ? [majorVersion] : undefined,
              }
            : skipToken,
    );
    const otaFile = otaData?.list.find((file) => file.buildId === details?.selectedId) ?? null;
    const [createRollout] = useCreateRolloutMutation();

    const validate = useValidationSchema(scheduleValidationSchema);

    const onSubmit = async (values: RolloutFormValues) => {
        if (!otaFile) {
            showErrorToast(t("sidepanels:rolloutPanel.noOtaFileFound"));
            return;
        }
        if (workspaceId === null) {
            showErrorToast(t("common:toast.noWorkspaceIdFound"));
            return;
        }

        let payload: CreateRolloutPayload["payload"] = {
            workspaceId,
            deviceIds: getSelectedDeviceIds(values),
        };

        const isTimeWindow = values.scheduleType === ScheduleType.TimeWindow;
        if (isTimeWindow && values.windowStart && values.windowEnd) {
            payload = Object.assign(payload, {
                installWindowStart: values.windowStart.toISOString(),
                installWindowEnd: values.windowEnd.toISOString(),
            });
        }

        if (values.notes) {
            payload = Object.assign(payload, { options: { notes: values.notes } });
        }

        const result = await createRollout({ codename: otaFile.codename, buildVersion: otaFile.version, payload });
        if ("data" in result) {
            showSuccessToast(t("sidepanels:rolloutPanel.toast.success"));
            dispatch(closeDetails());
        }
    };

    return (
        <Form<RolloutFormValues>
            onSubmit={onSubmit}
            validate={validate}
            initialValues={{ scheduleType: ScheduleType.WhenFound }}
            render={({ handleSubmit }) => (
                <form onSubmit={handleSubmit} className={localClasses.formLayout}>
                    <PaneElement.HeaderBox>
                        <Box>
                            <Typography variant="h6" className={classes.panelTitle}>
                                {t("rolloutPanel.title")}
                            </Typography>
                            {otaFile && <VersionMeta otaFile={otaFile} />}
                        </Box>
                        <Box className={classes.buttonBox}>
                            <SecondaryButton onClick={() => dispatch(closeDetails())}>
                                <i className="fas fa-times" />
                            </SecondaryButton>
                        </Box>
                    </PaneElement.HeaderBox>

                    <PaneElement.Container className={localClasses.containerWrapper}>
                        <Stepper activeStep={activeStep} className={localClasses.stepper}>
                            <Step>
                                <StepLabel>{t("rolloutPanel.steps.devices")}</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>{t("rolloutPanel.steps.schedule")}</StepLabel>
                            </Step>
                            <Step>
                                <StepLabel>{t("rolloutPanel.steps.confirm")}</StepLabel>
                            </Step>
                        </Stepper>

                        {activeStep === RolloutStep.Devices && otaFile ? (
                            <DeviceStep
                                otaFile={otaFile}
                                onNext={() => setActiveStep(RolloutStep.Schedule)}
                                onCancel={() => dispatch(closeDetails())}
                            />
                        ) : null}

                        {activeStep === RolloutStep.Schedule ? (
                            <ScheduleStep
                                onNext={() => setActiveStep(RolloutStep.Confirm)}
                                onBack={() => setActiveStep(RolloutStep.Devices)}
                            />
                        ) : null}

                        {activeStep === RolloutStep.Confirm ? (
                            <ConfirmStep onBack={() => setActiveStep(RolloutStep.Schedule)} />
                        ) : null}
                    </PaneElement.Container>
                </form>
            )}
        />
    );
};
